import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAwardBadges } from '../hooks/useAwardBadges';
import { Star, Target, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';
import RewardAnimationVideo from '@/components/ui/RewardAnimationVideo';

interface XPClaimPageProps {
  xpEarned?: number;
  timeSpent?: number;
  accuracy?: number;
}

const MOTIVATION_MESSAGES = [
  'همین استمرارهای کوچیکه که آدم‌ها رو متفاوت می‌کنه',
  'آینده از همین چند دقیقه‌ها ساخته میشه ادامه بده...',
  'قدم های کوچک،نتایج بزرگ میسازه',
  'این بخش هم مثل آب خوردن تموم شد',
  'تو فوق العاده ای،میدونستی !',
  'بابا تو دیگه کی هستی !',
  'عالیه همینجوری برو جلو',
];

const XPClaimPage: React.FC<XPClaimPageProps> = ({
  xpEarned = 5,
  timeSpent = 153,
  accuracy = 85
}) => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [showStats, setShowStats] = useState(false);
  const [motivation, setMotivation] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const awardBadgesMutation = useAwardBadges();

  const state = location?.state || {};
  const dynamicXp = state?.points ?? xpEarned;
  const dynamicAccuracy = state?.success_percentage ?? accuracy;
  const hasQuestions = (state?.total_questions ?? 0) > 0;
  const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  const toPersianNumber = (n: number | string) => toPersianDigits(String(n));
  
  // Check if we're in onboarding mode
  const isOnboarding = location.pathname.startsWith('/onboarding/');

  const displayTime = useMemo(() => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    let mm = 0;
    let ss = 0;
    if (typeof state?.time === 'string') {
      const parts = state.time.split(':');
      if (parts.length >= 2) {
        const pm = parseInt(parts[0], 10);
        const ps = parseInt(parts[1], 10);
        if (!Number.isNaN(pm) && !Number.isNaN(ps)) {
          mm = pm;
          ss = ps;
        }
      }
    }
    if (mm === 0 && ss === 0) {
      mm = Math.max(0, Math.floor(timeSpent));
      ss = 0;
    }
    const western = `${pad2(mm)}:${pad2(ss)}`;
    return toPersianDigits(western);
  }, [state?.time, timeSpent]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowStats(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // در حالت onboarding فقط پیام ثابت نمایش بده
    if (isOnboarding) {
      setMotivation('چه شروع طوفانی ای !');
    } else {
      setMotivation(MOTIVATION_MESSAGES[Math.floor(Math.random() * MOTIVATION_MESSAGES.length)]);
    }
  }, [isOnboarding]);

  const handleClaimXP = async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const streakState = location.state || {};

      // در حالت onboarding، مسیر متفاوت است
      if (isOnboarding) {
        // بررسی اینکه آیا نشان دریافت کرده یا نه
        try {
          const awardResult = await awardBadgesMutation.mutateAsync();
          
          if (awardResult?.count > 0 && awardResult?.newBadges?.length > 0) {
            // نشان دریافت کرده - به achievement برو
            navigate('/onboarding/achievement', { state: { awardResult }, replace: true });
          } else {
            // نشان دریافت نکرده - به streak برو
            navigate('/onboarding/streak', { replace: true });
          }
        } catch (e) {
          console.error('Error awarding badges in onboarding:', e);
          // در صورت خطا، به streak برو
          navigate('/onboarding/streak', { replace: true });
        }
        return;
      }

      // مسیر عادی برای حالت غیر onboarding
      if (streakState?.streak === true) {
        navigate('/learning/streak', { state: { ...streakState, fromReading: true }, replace: true });
        return;
      }

      const showDailyGoalBox = streakState.previous_minutes != null && streakState.daily_goal != null &&
        streakState.previous_minutes < streakState.daily_goal;

      const showStreakGoalBox = streakState.previous_streak != null && streakState.streak_goal != null &&
        streakState.previous_streak < streakState.streak_goal;

      if (showDailyGoalBox || showStreakGoalBox) {
        navigate('/learning/goals-reward', { state: streakState, replace: true });
        return;
      }

      const awardResult = await awardBadgesMutation.mutateAsync();

      if (awardResult?.count > 0 && awardResult?.newBadges?.length > 0) {
        navigate('/learning/achievement', { state: { awardResult }, replace: true });
      } else {
        navigate('/learning/challenges', { replace: true });
      }
    } catch (e) {
      if (isOnboarding) {
        navigate('/onboarding/streak', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  };

  const accuracyLabel = useMemo(() => {
    const a = Number(dynamicAccuracy);
    if (a > 70) return 'عالی';
    if (a >= 50) return 'خوب';
    if (a >= 30) return 'متوسط';
    return 'دقت پایین';
  }, [dynamicAccuracy]);

  const timeMinutes = useMemo(() => {
    if (typeof state?.time === 'string') {
      const parts = state.time.split(':');
      if (parts.length >= 2) {
        const pm = parseInt(parts[0], 10);
        const ps = parseInt(parts[1], 10);
        if (!Number.isNaN(pm) && !Number.isNaN(ps)) {
          return pm + ps / 60;
        }
      }
    }
    return Math.max(0, Number(timeSpent) || 0);
  }, [state?.time, timeSpent]);

  const timeLabel = useMemo(() => {
    if (timeMinutes < 6) return 'سریع';
    if (timeMinutes <= 10) return 'کمی سریع';
    return 'کند';
  }, [timeMinutes]);

  return (
    <div 
      className="min-h-[100dvh] bg-white text-gray-800 flex flex-col items-center justify-center px-4 overflow-x-hidden" 
      style={isOnboarding
        ? { paddingTop: 'calc(var(--onboarding-header-h, 128px) + 10px)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }
        : { paddingTop: '1.5rem', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      dir="rtl"
    >
      <div className="flex w-full flex-col items-center translate-y-4">
        <motion.div
          initial={{ scale: 0.7, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: 'spring', bounce: 0.35 }}
          className="relative mb-1 w-full max-w-[315px] h-[240px] flex items-center justify-center overflow-hidden"
        >
          <RewardAnimationVideo size={220} />
        </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.55 }}
        className="text-center mb-8 min-h-[42px] flex items-center px-4"
      >
        <p className="text-gray-700 text-lg md:text-xl font-persian leading-relaxed max-w-md">{motivation}</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: showStats ? 1 : 0, y: showStats ? 0 : 40 }}
        transition={{ duration: 0.6 }}
        className={`flex ${hasQuestions ? 'justify-between gap-3' : 'justify-center gap-3'} mb-8 w-full max-w-[336px]`}
      >
        <div className={`bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden ${hasQuestions ? 'flex-1' : 'w-[104px]'}`}>
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 px-2 py-1.5">
            <div className="text-white text-[11px] font-bold font-persian text-center">امتیاز کل</div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-2">
            <div className="flex flex-row-reverse items-center justify-center gap-1.5">
              <Star className="w-[18px] h-[18px] text-yellow-500" fill="currentColor" />
              <div className="text-lg font-bold text-yellow-600 font-persian">{toPersianNumber(dynamicXp)}</div>
            </div>
          </div>
        </div>

        {hasQuestions && (
          <div className="bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden flex-1">
            <div className="bg-gradient-to-r from-green-400 to-green-500 px-2 py-1.5">
              <div className="text-white text-[11px] font-bold font-persian text-center">{accuracyLabel}</div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-2">
              <div className="flex flex-row-reverse items-center justify-center gap-1.5">
                <Target className="w-[18px] h-[18px] text-green-500" />
                <div className="text-lg font-bold text-green-600 font-persian">{toPersianNumber(dynamicAccuracy)}٪</div>
              </div>
            </div>
          </div>
        )}

        <div className={`bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden ${hasQuestions ? 'flex-1' : 'w-[104px]'}`}>
          <div className="bg-gradient-to-r from-blue-400 to-blue-500 px-2 py-1.5">
            <div className="text-white text-[11px] font-bold font-persian text-center">{timeLabel}</div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center p-2">
            <div className="flex flex-row-reverse items-center justify-center gap-1.5">
              <Clock className="w-[18px] h-[18px] text-blue-500" />
              <div className="text-lg font-bold text-blue-600 font-persian">{displayTime}</div>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: showStats ? 1 : 0, scale: showStats ? 1 : 0.8 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClaimXP}
        disabled={isNavigating}
        className={cn(
          'w-[84%] max-w-[350px] py-3.5 rounded-xl text-center font-bold font-persian text-[15px] transition-all duration-300',
          'active:scale-95 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3',
          isNavigating ? 'cursor-not-allowed opacity-70' : '',
          'bg-[#6e61ff] text-white hover:bg-[#5a4ed9]'
        )}
      >
        {isNavigating ? (
          <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <span>دریافت امتیاز</span>
        )}
      </motion.button>
      </div>
    </div>
  );
};

export default XPClaimPage;
